import { useEffect, useState } from "react";
import { getAiStatus, setAiEnabled } from "../lib/api.ts";
import type { AiStatus } from "@currencycore/shared";
import { toast } from "./Toast.tsx";

const AI_ENDPOINT = "https://api.currency-core.com/v1/ai?q=Convert 50 EUR to INR";

/**
 * AI endpoint panel, shown on the Developer page next to API keys and the MCP
 * panel. Always visible (there's no connect handshake) and enabled by default;
 * the switch turns the `/v1/ai` endpoint on or off without revoking keys. AI
 * calls consume the separate AI credit pool (1 credit each) and never count
 * against the request quota; the monthly credit allowance is capped per plan.
 */
export function AiPanel({ orgId }: { orgId: string }) {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    let active = true;
    void getAiStatus(orgId)
      .then((s) => {
        if (active) {
          setStatus(s);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  async function toggle(next: boolean) {
    setSaving(true);
    try {
      await setAiEnabled(orgId, next);
      setStatus((s) => (s ? { ...s, enabled: next } : s));
      toast(next ? "AI endpoint enabled." : "AI endpoint disabled.", true);
    } catch {
      toast("Couldn't update the AI endpoint.");
    } finally {
      setSaving(false);
    }
  }

  function copyUrl() {
    void navigator.clipboard?.writeText(AI_ENDPOINT).then(
      () => toast("Endpoint copied.", true),
      () => toast("Couldn't copy. Please copy it manually."),
    );
  }

  const enabled = status?.enabled ?? true;
  const globalOff = !!status && !status.globalEnabled;
  const disabledMessage = status?.disabledMessage ?? null;
  const aiRequests = status?.aiRequests ?? 0;
  const requests = status?.requests ?? 0;
  const pct = requests > 0 ? Math.round((aiRequests / requests) * 100) : 0;
  const sub =
    status?.aiLimit != null
      ? `${aiRequests.toLocaleString()} / ${status.aiLimit.toLocaleString()} this month (Hobby)`
      : `${pct}% of ${requests.toLocaleString()} requests this period`;

  return (
    <div className="card mt rise">
      <div className="card-head">
        <h2>AI endpoint</h2>
        {loaded && (
          <span className={`badge ${globalOff || !enabled ? "badge-neutral" : "badge-accent"}`} style={{ fontSize: "10px" }}>
            {globalOff ? "Unavailable" : enabled ? "Enabled" : "Disabled"}
          </span>
        )}
      </div>
      <div className="card-pad">
        <p className="muted" style={{ marginTop: 0 }}>
          Ask currency questions in plain English. The model uses your live rates to answer and convert.
          Each call consumes 1 AI credit from your separate AI credit pool (it doesn't count against your request quota)
          {status?.aiLimit != null ? `, with ${status.aiLimit.toLocaleString()} credits / month on Hobby` : ""}.{" "}
          <a
            href="https://currency-core.com/docs/ai-endpoint"
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--accent-text)" }}
          >
            Docs →
          </a>
        </p>

        {globalOff && (
          <div className="auth-banner auth-banner-info" style={{ marginTop: "12px" }}>
            The AI endpoint is currently disabled platform-wide{disabledMessage ? `: ${disabledMessage}` : "."}
          </div>
        )}

        <div className="secret-box">
          <code>{AI_ENDPOINT}</code>
          <button className="btn btn-sm" onClick={copyUrl} type="button">Copy</button>
        </div>

        {loaded && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "28px", flexWrap: "wrap", marginTop: "16px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "var(--text-subtle)" }}>AI requests</div>
                <div style={{ fontSize: "20px", fontWeight: 600, color: "var(--text)" }}>
                  {aiRequests.toLocaleString()}
                </div>
                <div style={{ fontSize: "11px", color: "var(--text-faint)" }}>{sub}</div>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                <span className="hint">{enabled ? "AI endpoint on" : "AI endpoint off"}</span>
                <label className="switch" aria-label="Enable AI endpoint">
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={saving || globalOff}
                    onChange={(e) => void toggle(e.target.checked)}
                  />
                  <span className="track" />
                  <span className="thumb" />
                </label>
              </div>
            </div>
            {!enabled && (
              <p className="muted" style={{ fontSize: "12.5px", marginTop: "12px", marginBottom: 0 }}>
                The AI endpoint is off. <code>/v1/ai</code> returns 403 until you turn it back on. Nothing else is affected.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
